<template>
  <div class="relative overflow-hidden min-h-screen py-12 px-4 sm:px-6 lg:px-8 flex justify-center">
    <div v-reveal class="w-full max-w-3xl card p-6 sm:p-8 md:p-10 z-20 self-start">
      <!-- Header -->
      <div class="text-center mb-8">
        <h2 class="font-serif text-4xl font-bold text-gray-900">Suggestions</h2>
        <div class="mt-4 w-14 h-1 rounded-full bg-brand-red mx-auto"></div>
      </div>

      <!-- Notification/Message Banner -->
      <transition name="fade">
        <div
          v-if="message"
          :class="[
            'p-3 text-center text-sm font-semibold mb-6 rounded-md transition-opacity duration-300',
            messageType === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
          ]"
        >
          {{ message }}
        </div>
      </transition>

      <!-- Suggestions List -->
      <div class="space-y-4 mb-10">
        <div v-if="loadingSuggestions" class="flex items-center justify-center gap-3 p-8 text-gray-500">
          <div class="animate-spin rounded-full h-5 w-5 border-2 border-gray-200 border-t-brand-red"></div>
          Loading suggestions...
        </div>
        <div v-else-if="comments.length === 0" class="text-center p-8 text-gray-500 border border-dashed border-gray-300 rounded-md">
          No suggestions posted yet. Be the first!
        </div>
        <!-- Note the use of comment.user_email and comment.suggestion_text from the API -->
        <div v-else v-for="comment in comments" :key="comment.id" v-reveal="{ y: 20 }" class="rounded-md border border-gray-100 bg-gray-50/70 p-5">
          <div class="flex items-center mb-3">
            <div class="h-10 w-10 rounded-full bg-brand-red flex items-center justify-center text-white font-semibold flex-shrink-0">
              {{ comment.user_email.charAt(0).toUpperCase() }}
            </div>
            <div class="ml-3 min-w-0">
              <p class="font-semibold text-gray-900 truncate">{{ comment.user_email }}</p>
              <p class="text-xs text-gray-500">{{ formatDate(comment.created_at) }}</p>
            </div>
          </div>
          <p class="text-gray-700 leading-relaxed">{{ comment.suggestion_text }}</p>
        </div>
      </div>

      <!-- Comment Input Form or Login Message -->
      <div class="pt-8 border-t border-gray-100">
        <h3 class="font-serif text-2xl font-bold text-gray-900 mb-4">Post a Comment</h3>

        <div v-if="isLoggedIn">
          <form @submit.prevent="postComment">

            <div class="mb-4">
              <label class="block text-sm text-gray-600">
                Commenting as: <span class="font-semibold text-brand-red">{{ currentUser.email || 'User' }}</span>
              </label>
            </div>

            <div class="mb-5">
              <label for="comment" class="form-label">Suggestion</label>
              <textarea
                v-model="newComment.text"
                id="comment"
                rows="4"
                class="form-input resize-y"
                placeholder="Share your suggestion..."
                required
              ></textarea>
            </div>
            <button
              type="submit"
              :disabled="posting"
              class="btn-primary w-full sm:w-auto px-7"
            >
              {{ posting ? 'Posting...' : 'Post Suggestion' }}
            </button>
          </form>
        </div>

        <div v-else class="text-center p-8 border-2 border-dashed border-gray-200 rounded-md bg-gray-50">
          <i class="fa-solid fa-lock text-brand-red text-xl mb-3"></i>
          <p class="text-lg text-brand-red font-semibold mb-1">Login Required</p>
          <p class="text-gray-600">You must be logged in to post a comment or suggestion.</p>
          <a href="/login" class="btn-outline mt-5">
            Go to Login Page
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { authHeaders } from '@/api'
// Utility function to check authentication status from localStorage
function getAuthStatus() {
  const token = localStorage.getItem("token");
  const userString = localStorage.getItem("user");
  
  if (token && userString) {
    try {
      // Parse the user object saved during login
      return { isLoggedIn: true, user: JSON.parse(userString) };
    } catch (e) {
      console.error("Error parsing user data from localStorage:", e);
      return { isLoggedIn: false, user: null };
    }
  }
  return { isLoggedIn: false, user: null };
}

export default {
  data() {
    return {
      comments: [], // Initialized as empty, will be populated by API
      newComment: {
        text: ""
      },
      // State properties for authentication
      isLoggedIn: false,
      currentUser: {},
      loadingSuggestions: true, // New state for loading indicator
      posting: false, // New state for post button status
      message: "", // New state for user feedback messages
      messageType: "error", // 'success' or 'error'
      // IMPORTANT: Replace this with your actual API base URL if different
      baseUrl: "https://sanskritirdapoon.in", 
    };
  },
  mounted() {
    // Initial check for authentication status
    this.updateAuthStatus();
    this.fetchSuggestions(); // Fetch existing suggestions from the DB
    
    // Listen for storage changes (e.g., login/logout in another tab)
    window.addEventListener('storage', this.updateAuthStatus);
  },
  beforeUnmount() {
    // Clean up the event listener to prevent memory leaks
    window.removeEventListener('storage', this.updateAuthStatus);
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    showMessage(msg, type = "error") {
      this.message = msg;
      this.messageType = type;
      setTimeout(() => {
        this.message = "";
      }, 4000); // Clear message after 4 seconds
    },
    updateAuthStatus() {
      const { isLoggedIn, user } = getAuthStatus();
      this.isLoggedIn = isLoggedIn;
      // Set currentUser to the parsed user object or an empty object if logged out
      this.currentUser = user || {}; 
    },
    
    // New method to fetch suggestions from the API
    async fetchSuggestions() {
      this.loadingSuggestions = true;
      try {
        const res = await fetch(`${this.baseUrl}/api/suggestions`);
        if (res.ok) {
          // Assuming API returns an array of objects matching your DB structure
          const allSuggestions = await res.json();
          // Filter only approved suggestions
          this.comments = allSuggestions.filter(s => s.is_approved);
        } else {
          const data = await res.json();
          this.showMessage(data.error || 'Failed to load suggestions.', 'error');
        }
      } catch (err) {
        console.error("Fetch suggestions error:", err);
        this.showMessage('Connection error while fetching suggestions.', 'error');
      } finally {
        this.loadingSuggestions = false;
      }
    },
    
    // Updated method to post comments to the API
    async postComment() {
      if (!this.isLoggedIn || !this.currentUser.email || !this.newComment.text) {
          this.showMessage("You must be logged in and provide text.", "error");
          return;
      }

      this.posting = true;
      
      const payload = {
        user_email: this.currentUser.email, // Sent to the server for insertion
        text: this.newComment.text,
      };

      try {
        const res = await fetch(`${this.baseUrl}/api/suggestions`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (res.ok) {
          this.showMessage(data.message || 'Suggestion posted!', 'success');
          this.newComment.text = "";
          
          // Prepend the new suggestion from the API response to the list
          this.comments.unshift({
            id: data.id,
            user_email: data.user_email,
            suggestion_text: data.text,
            created_at: new Date().toISOString() // Use a placeholder date for immediate rendering
          });
          
        } else {
          this.showMessage(data.error || 'Failed to post suggestion to the database.', 'error');
        }
      } catch (err) {
        console.error("Post suggestion error:", err);
        this.showMessage('Network error while posting suggestion.', 'error');
      } finally {
        this.posting = false;
      }
    }
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
